import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState, KpiCard } from "@/components/common";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Plus, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const CATS = ["Rent", "Salaries", "Electricity", "Marketing", "Purchases", "Transportation", "Software", "Maintenance", "Other"];
const CHART = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#14B8A6", "#F97316"];

export default function Expenses() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Rent", amount: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/expenses").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Loader label="Loading expenses…" />;

  const add = async () => {
    setSaving(true);
    try { await api.post("/expenses", { ...form, amount: +form.amount }); toast.success("Expense recorded"); setOpen(false); setForm({ category: "Rent", amount: "", description: "" }); load(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Track every rupee going out and where it goes.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-expense-btn" className="font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button></DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Record Expense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="expense-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Amount ₹</Label><Input type="number" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={add} disabled={saving || !form.amount} data-testid="save-expense-btn" className="w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Where money goes" subtitle="All-time by category" testid="expense-chart">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {data.breakdown.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
        <SectionCard title="Breakdown" className="lg:col-span-2" testid="expense-breakdown-list">
          <div className="space-y-3">
            {data.breakdown.map((e, i) => {
              const pct = data.total ? (e.value / data.total) * 100 : 0;
              return (
                <div key={e.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART[i % CHART.length] }} />{e.name}</span>
                    <span className="font-semibold tabular-nums">{formatINR(e.value)} · {pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART[i % CHART.length] }} /></div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Expense Log" testid="expenses-table">
        {data.expenses.length === 0 ? <EmptyState icon={Wallet} title="No expenses" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold py-3">Category</th>
                <th className="text-left font-semibold py-3">Description</th>
                <th className="text-left font-semibold py-3 hidden sm:table-cell">Date</th>
                <th className="text-left font-semibold py-3 hidden md:table-cell">By</th>
                <th className="text-right font-semibold py-3">Amount</th>
              </tr></thead>
              <tbody>
                {data.expenses.slice(0, 60).map((e) => (
                  <tr key={e.id} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
                    <td className="py-3 font-medium">{e.category}</td>
                    <td className="py-3 text-muted-foreground">{e.description || "—"}</td>
                    <td className="py-3 hidden sm:table-cell text-muted-foreground">{formatDate(e.date)}</td>
                    <td className="py-3 hidden md:table-cell text-muted-foreground">{e.created_by}</td>
                    <td className="py-3 text-right font-semibold tabular-nums">{formatINR(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
